import * as React from "react";
import { IconButton, DatePicker, Dropdown, IDropdownOption, ResponsiveMode } from "@fluentui/react";
import { DayPickerStrings, firstDayOfWeek } from "../../config/dateSettings";
import { DateService } from "../../services/dateService";
import { InputProps } from "../../interfaces/props/InputProps";
// import * as lcid from "lcid";
import * as lcid from "../../../../utilities/lcid/lcid";
import { logger } from "../../../../utilities/logger";
import { LanguageContext } from "../../../../utilities/LocalizationModule";
import { clearButtonClass } from "../../config/controlStyles";
import dayjs from "dayjs";
import { SharePointMetadataBooleans } from "../../config/sharepointConstants";
import { clearButtonProps, datePickerStyles, iconButtonProps, iconButtonStyles, textFieldStyles } from "../../styles/datePickerStyles";

export const DateTime: React.FunctionComponent<InputProps> = ({
  displayName,
  isRequired,
  inputValue,
  error,
  updateInputValidationStatus,
  inputValidator,
  regionalSetting
}: InputProps) => {
  const languageStrings = React.useContext(LanguageContext);
  const [timeOptions] = React.useState<IDropdownOption[]>(DateService.getHours());
  const [time, setTime] = React.useState<any>();
  const [dateFormat] = React.useState<Intl.DateTimeFormat>(new Intl.DateTimeFormat(lcid.from(regionalSetting?.CollationLCID).replace("_", "-")));

  const isFirstRun = React.useRef(true);
  React.useEffect(() => {
    try {
      if (inputValue?.value) {
        let selectedDate = dayjs(inputValue?.value);
        if (selectedDate) {
          const hours = selectedDate?.hour();
          const minutes = selectedDate?.minute();
          setTime(`${hours}:${minutes == 0 ? "00" : minutes}`);
        }
      }
    } catch (e) {
      logger(e, "dateTime.tsx (useEffect(inputValue?.value))")
    }
  }, [inputValue?.value]);

  React.useEffect(() => {
    try {
      if (isFirstRun.current) {
        isFirstRun.current = false;
        return;
      }
      updateInputValidationStatus(inputValidator.validate(inputValue?.value));
    } catch (e) {
      logger(e, "dateTime.tsx (useEffect)")
    }
  }, [inputValue?.updatedFromDataSourceToggle]);

  const setTimePart = (date: Date, selectedTime: string = null) => {
    if (date) {
      const [hour, minutes] = !selectedTime ? time.split(':') : selectedTime.split(':');
      date.setHours(+hour, +minutes, 0);
    }
  }

  const onDropdownChange = (event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
    setTime(item.key);

    if (inputValue?.value) {
      let selectedDate = new Date(inputValue.value);
      if (selectedDate) {
        setTimePart(selectedDate, item.key as string);
      }
      updateInputValidationStatus(inputValidator.validate(selectedDate));
    }
  };

  const onSelectDate = (date: Date | null | undefined): void => {
    if (!date) {
      setTime(undefined);
    }
    else if (time) {
      setTimePart(date);
    }

    updateInputValidationStatus(inputValidator.validate(date));
  };

  const onFormatDate = React.useCallback((date?: Date): string => {
    return !date || date?.getTime() !== date?.getTime() ? '' : dateFormat.format(date);
  }, [dateFormat]);

  const onClearClick = (ev: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
    if (!ev.defaultPrevented) {
      onSelectDate(undefined);
      ev.stopPropagation();
      ev.preventDefault();
    }
  };

  return (
    <>
      <DatePicker
        label={displayName}
        disabled={inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
        isRequired={isRequired()}
        allowTextInput
        parseDateFromString={DateService.parseDateFromString}
        firstDayOfWeek={firstDayOfWeek}
        strings={DayPickerStrings}
        value={inputValue?.value}
        placeholder={languageStrings.EnterDate}
        ariaLabel={languageStrings.EnterDate}
        showMonthPickerAsOverlay={true}
        onSelectDate={onSelectDate}
        formatDate={onFormatDate}
        className="xb-sp-input"
        textField={{
          errorMessage: error,
          onRenderSuffix: () => {
            return (
              <div className={clearButtonClass}>
                <IconButton
                  styles={iconButtonStyles}
                  iconProps={iconButtonProps}
                  {...clearButtonProps}
                  onClick={onClearClick}
                />
              </div>)
          },
          styles: textFieldStyles
        }}
        styles={datePickerStyles}
      />
      <div style={{ marginTop: 10 }}></div>
      <Dropdown
        disabled={!inputValue?.value || inputValue?.xml?.ReadOnly === SharePointMetadataBooleans.true}
        options={timeOptions}
        selectedKey={time ?? null}
        onChange={onDropdownChange}
        className="xb-sp-input"
        // responsiveMode={ResponsiveMode.small}
      />
    </>
  );
};

export default React.memo(DateTime);